<template>
  <div class="am-root" :class="{ 'am-playing': playing }">
    <audio :src="src" ref="audio" preload="metadata"></audio>

    <button class="am-play" @click.stop.prevent="togglePlay" :aria-pressed="playing" :title="playing ? 'Pausar' : 'Reproducir'">
      <svg v-if="!playing" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2"><polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/></svg>
      <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
    </button>

    <div class="am-wave" @click.stop.prevent="seek($event)">
      <div class="am-wave-bg" aria-hidden="true"></div>
      <div class="am-wave-progress" :style="{ width: progress + '%' }" aria-hidden="true"></div>
    </div>

    <div class="am-times">
      <span class="am-time am-time--current">{{ formatTime(currentTime) }}</span>
      <span class="am-time am-time--sep">/</span>
      <span class="am-time am-time--total">{{ formatTime(duration) }}</span>
    </div>

    <img v-if="avatarUrl" :src="avatarUrl" class="am-avatar" alt="avatar" />
  </div>
</template>

<script>
export default {
  name: 'AudioMessage',
  props: {
    src: { type: String, required: true },
    avatarUrl: { type: String, default: '' },
    msg: { type: Object, default: null }
  },
  data() {
    return {
      id: `am-${Date.now()}-${Math.floor(Math.random()*10000)}`,
      playing: false,
      duration: 0,
      currentTime: 0,
      progress: 0,
      loaded: false,
    }
  },
  mounted() {
    const a = this.$refs.audio
    if (!a) return
    a.addEventListener('loadedmetadata', this.onLoaded)
    a.addEventListener('timeupdate', this.onTimeUpdate)
    a.addEventListener('ended', this.onEnded)
    window.addEventListener('audio-play', this.onAudioPlayEvent)
  },
  beforeDestroy() {
    const a = this.$refs.audio
    if (a) {
      a.removeEventListener('loadedmetadata', this.onLoaded)
      a.removeEventListener('timeupdate', this.onTimeUpdate)
      a.removeEventListener('ended', this.onEnded)
    }
    window.removeEventListener('audio-play', this.onAudioPlayEvent)
  },
  methods: {
    onLoaded() {
      const a = this.$refs.audio
      this.duration = a.duration || 0
      this.loaded = true
    },
    onTimeUpdate() {
      const a = this.$refs.audio
      this.currentTime = a.currentTime || 0
      this.progress = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0
    },
    onEnded() {
      this.playing = false
      this.currentTime = this.duration
      this.progress = 100
    },
    togglePlay() {
      const a = this.$refs.audio
      if (!a) return
      if (a.paused) {
        // notify others to pause
        window.dispatchEvent(new CustomEvent('audio-play', { detail: { id: this.id } }))
        a.play().catch(() => {})
        this.playing = true
      } else {
        a.pause()
        this.playing = false
      }
    },
    onAudioPlayEvent(e) {
      const otherId = e && e.detail && e.detail.id
      if (!otherId) return
      if (otherId !== this.id) {
        const a = this.$refs.audio
        if (a && !a.paused) {
          a.pause()
          this.playing = false
        }
      }
    },
    seek(e) {
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left
      const ratio = Math.max(0, Math.min(1, x / rect.width))
      const a = this.$refs.audio
      if (a && this.duration > 0) {
        a.currentTime = this.duration * ratio
        this.currentTime = a.currentTime
        this.progress = ratio * 100
      }
    },
    formatTime(sec) {
      if (!sec || Number.isNaN(sec)) sec = 0
      sec = Math.floor(sec)
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      const s = sec % 60
      const pad = (v) => (v < 10 ? '0' + v : '' + v)
      if (h > 0) return `${h}:${pad(m)}:${pad(s)}`
      return `${m}:${pad(s)}`
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');
.am-root { display:flex; align-items:center; gap:10px; background:#222; color:#fff; padding:8px 10px; border-radius:10px; max-width:360px; }
.am-play { width:38px; height:38px; border-radius:50%; border:1px solid rgba(255,255,255,0.06); background:transparent; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#fff; flex:0 0 38px; }
.am-wave { position:relative; height:36px; flex:1; cursor:pointer; display:flex; align-items:center; }
.am-wave-bg { position:absolute; inset:0; border-radius:8px; background-image: repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 3px, transparent 3px 6px); background-size:6px 100%; }
.am-wave-progress { position:absolute; left:0; top:0; bottom:0; border-radius:8px; overflow:hidden; background: linear-gradient(90deg, rgba(34,197,94,0.18), rgba(34,197,94,0.08)); pointer-events:none; }
.am-times { display:flex; align-items:center; gap:6px; font-size:12px; color:rgba(255,255,255,0.9); flex-shrink:0; }
.am-time { font-weight:700; font-variant-numeric: tabular-nums; }
.am-avatar { width:36px; height:36px; border-radius:50%; object-fit:cover; flex-shrink:0; }
.am-root.am-playing .am-wave-progress { box-shadow: inset 0 0 18px rgba(34,197,94,0.06); }
</style>
